import type { AIClient, AIAnalysisRequest, AIAnalysisResult } from '$lib/types/ai';
import { calculateMaxTokens, getDefaultTemperature } from '$lib/utils/tokenUtils';
import { usageTracker } from '../../UsageTrackingService';
import { HttpClient } from '$lib/utils/httpClient';

/**
 * OpenRouter proxy client with natural language prompts
 * Matches desktop implementation
 */
export class OpenRouterClient implements AIClient {
  private serverUrl: string;
  private authToken: string = '';
  private deviceId: string = '';
  private initPromise: Promise<void>;

  constructor(private model: string, private maxTokens: number = 2000) {
    // Use relative path in development (Vite proxy), absolute in production
    const isDev = import.meta.env.DEV;
    this.serverUrl = isDev 
      ? '/api/openrouter/proxy'  // Vite will proxy this
      : 'https://appstore.junlim.org/api/openrouter/proxy';
    
    // Initialize auth credentials and store the promise
    this.initPromise = this.initializeAuth().catch(console.error);
  }

  private async initializeAuth() {
    // Get or generate device ID
    this.deviceId = localStorage.getItem('deviceId') || this.generateDeviceId();
    if (!localStorage.getItem('deviceId')) {
      localStorage.setItem('deviceId', this.deviceId);
    }
    
    // Always request new auth token on initialization to ensure license is recognized
    // Old tokens may be cached without license information
    // Request new auth token on initialization
    await this.requestAuthToken();
  }
  
  private async requestAuthToken() {
    // Clear old token to force new one
    localStorage.removeItem('authToken');
    
    const isDev = import.meta.env.DEV;
    const authUrl = isDev 
      ? '/api/auth/token'  // Vite will proxy this
      : 'https://appstore.junlim.org/api/auth/token';
    
    // Check if user has a valid license
    let licenseKey: string | null = null;
    try {
      // Get active license from secure storage
      const { LicenseServiceFactory } = await import('$lib/services/license/LicenseServiceFactory');
      const licenseServices = LicenseServiceFactory.createLicenseServices();
      const licenseKeys = await licenseServices.storage.listLicenseKeys();
      
      if (licenseKeys.length > 0) {
        licenseKey = licenseKeys[0]; // Use first available license key
        // Found license key
      } else {
        // No license found, using free tier
      }
    } catch (e) {
      console.error('[OpenRouterProxy] Error accessing license storage:', e);
    }
      
    // Get current subscription tier
    let subscriptionTier = 'free';
    try {
      const { SubscriptionService } = await import('../../subscription/SubscriptionService');
      const subscriptionService = SubscriptionService.getInstance();
      const status = subscriptionService.getStatus();
      subscriptionTier = status.type; // 'free', 'trial', or 'premium'
    } catch (e) {
      console.error('[OpenRouterProxy] Error getting subscription status:', e);
    }
    
    const requestBody = {
      deviceId: this.deviceId,
      licenseKey: licenseKey,  // Include license key if available
      tier: subscriptionTier    // Include subscription tier
    };
    
    // Request auth token with license if available
    
    const httpClient = HttpClient.getInstance();
    
    try {
      const data = await httpClient.request<any>(authUrl, {
        method: 'POST',
        body: requestBody,
        maxRetries: 3,
        retryDelay: 2000
      });
      
      if (data.success && data.token) {
        this.authToken = data.token;
        localStorage.setItem('authToken', data.token);
      } else {
        console.error('[OpenRouterProxy] Failed to get auth token');
        // Clear stored token on auth failure
        this.authToken = '';
        localStorage.removeItem('authToken');
      }
    } catch (error: any) {
      console.error('[OpenRouterProxy] Failed to get auth token:', error);
      // Clear stored token on auth failure
      this.authToken = '';
      localStorage.removeItem('authToken');
      
      // For 401, we need proper authentication
      if (error.status === 401) {
        throw new Error('Authentication failed - license may be invalid');
      }
      // Don't throw for other errors, just continue without token
    }
  }

  private generateDeviceId(): string {
    return 'svelte-' + Math.random().toString(36).substring(2, 15);
  }

  async sendMessage(prompt: string): Promise<string> {
    // Ensure initialization is complete
    await this.initPromise;
    
    // Check if this is an AI error analysis prompt (for analyzing specific errors)
    const isErrorAnalysis = prompt.includes('JSON으로 답하세요:') || 
                           prompt.includes('JSON 형식으로 응답:') ||
                           prompt.includes('"agrees"') ||
                           prompt.includes('맞춤법 오류를 분석');
    
    if (isErrorAnalysis) {
      // This is an AI error analysis request - pass it directly to the model
      // Extract suggestions from prompt to help with duplicate filtering
      let systemPrompt = this.isReasoningModel() 
        ? `You are a Korean grammar expert. Your task is to analyze Korean text corrections.

## Instructions:
1. Carefully analyze the suggested correction
2. Determine if the correction is valid
3. Provide alternatives if needed
4. Return your response in JSON format

## Response Format:
{
  "agrees": boolean,
  "comment": "Brief explanation in Korean",
  "alternatives": ["alternative1", "alternative2"],
  "confidence": number (0-1)
}`
        : 'Korean grammar expert. Analyze the correction suggestion and respond in JSON format.';
      
      // Try to extract the suggested correction from the prompt for better duplicate filtering
      const suggestionMatch = prompt.match(/제안된 수정: "([^"]+)"/); 
      if (suggestionMatch) {
        const additional = `\n중요: alternatives에는 "${suggestionMatch[1]}"과 다른 대안만 포함하세요. "${suggestionMatch[1]}"과 같은 대안은 절대 alternatives에 포함하지 마세요.`;
        systemPrompt = this.isReasoningModel()
          ? systemPrompt.replace('## Response Format:', `## Important Note:\n- Do not include "${suggestionMatch[1]}" in alternatives\n- Only provide different alternatives\n\n## Response Format:`)
          : `Korean grammar expert. Analyze the correction suggestion and respond in JSON format.${additional}`;
      }
      
      const response = await this.makeProxyRequest([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: prompt }
      ]);
      
      return response.choices?.[0]?.message?.content || '{"agrees": true, "comment": "분석 완료", "alternatives": [], "confidence": 0.8}';
    }
    
    // Otherwise, this is a grammar check request
    // Parse the text to analyze from the prompt
    // Try multiple patterns to extract the text
    let textToAnalyze = '';
    
    // Pattern 1: Text to analyze:
    let textMatch = prompt.match(/Text to analyze:\s*\n"([^"]+)"/);
    if (textMatch) {
      textToAnalyze = textMatch[1];
    } else {
      // Pattern 2: Korean text:
      textMatch = prompt.match(/Korean text:\s*\n\n"([^"]+)"/);
      if (textMatch) {
        textToAnalyze = textMatch[1];
      } else {
        // Pattern 3: Any quoted text after colon
        textMatch = prompt.match(/:\s*\n+"([^"]+)"/);
        if (textMatch) {
          textToAnalyze = textMatch[1];
        }
      }
    }
    
    if (!textToAnalyze) {
      console.warn('[OpenRouterProxy] Could not extract text to analyze from prompt');
      return '{"errors": []}';
    }
    
    // Create a prompt based on model type
    const naturalPrompt = this.isReasoningModel()
      ? this.createReasoningPrompt(textToAnalyze)
      : this.createStandardPrompt(textToAnalyze);
    
    // Use appropriate system message based on model type
    const systemPrompt = this.isReasoningModel()
      ? this.getReasoningSystemPrompt()
      : 'Korean grammar expert. Output JSON only. No reasoning. Brief explanations.';
    
    // Override maxTokens for text analysis to ensure complete response
    const originalMaxTokens = this.maxTokens;
    this.maxTokens = this.isReasoningModel() ? 4000 : 2000; // More tokens for reasoning models
    
    const response = await this.makeProxyRequest([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: naturalPrompt }
    ]);
    
    // Restore original maxTokens
    this.maxTokens = originalMaxTokens;
    
    let content = response.choices?.[0]?.message?.content || '';
    
    // Handle GPT-OSS specific response patterns
    if (this.model.includes('gpt-oss')) {
      // Check for tool_calls response (GPT-OSS specific)
      if (!content && response.choices?.[0]?.message?.tool_calls) {
        const toolCall = response.choices[0].message.tool_calls[0];
        if (toolCall?.function?.arguments) {
          try {
            content = toolCall.function.arguments;
          } catch (e) {
            console.error('[OpenRouterProxy] Failed to parse tool call arguments:', e);
          }
        }
      }
      
      // For GPT-OSS with reasoning but no content, try to extract errors from reasoning
      if (!content && response.choices?.[0]?.message?.reasoning) {
        const reasoning = response.choices[0].message.reasoning;
        
        // Try to extract error information from reasoning text
        const errors: Array<{original: string; correction: string; explanation: string}> = [];
        
        // Common errors found in reasoning
        const commonErrors = [
          { pattern: /차가/, original: '차가', correction: '차이가', explanation: '조사 누락' },
          { pattern: /어려웠을뿐더러/, original: '어려웠을뿐더러', correction: '어려웠을 뿐더러', explanation: '띄어쓰기' },
          { pattern: /전셋집/, original: '전셋집', correction: '전세집', explanation: '맞춤법' },
          { pattern: /대출로 끼고서 였다/, original: '대출로 끼고서 였다', correction: '대출을 끼고서였다', explanation: '조사 누락 및 띄어쓰기' },
          { pattern: /무거워 졌다/, original: '무거워 졌다', correction: '무거워졌다', explanation: '띄어쓰기' },
          { pattern: /조건 맞는/, original: '조건 맞는', correction: '조건에 맞는', explanation: '조사 누락' },
          { pattern: /빚내서/, original: '빚내서', correction: '빚을 내서', explanation: '조사 누락' },
          { pattern: /마음에 든다 했다/, original: '마음에 든다 했다', correction: '마음에 든다고 했다', explanation: '인용 조사' }
        ];
        
        // Check if reasoning mentions these errors
        for (const error of commonErrors) {
          if (reasoning.includes(error.original) || reasoning.match(error.pattern)) {
            errors.push({
              original: error.original,
              correction: error.correction,
              explanation: error.explanation
            });
            if (errors.length >= 8) break; // Limit to 8 errors
          }
        }
        
        if (errors.length > 0) {
          content = JSON.stringify({ errors });
        } else {
          return '{"errors": []}';
        }
      }
    }
    
    // If still empty, return a default response
    if (!content || content.trim() === '') {
      console.warn('[OpenRouterProxy] Empty response from model, returning default');
      return '{"errors": []}';
    }
    
    return content;
  }

  async analyzeCorrections(request: AIAnalysisRequest): Promise<AIAnalysisResult[]> {
    const startTime = Date.now();

    try {
      // Ensure initialization is complete
      await this.initPromise;

      // If no corrections to analyze, return empty array
      if (!request.corrections || request.corrections.length === 0) {
        return [];
      }

      // For large correction sets, process in batches to avoid timeout and response truncation
      const BATCH_SIZE = 5; // Process 5 corrections at a time for fastest first response
      const totalCorrections = request.corrections.length;

      if (totalCorrections > BATCH_SIZE) {
        const results: AIAnalysisResult[] = [];
        const batches = Math.ceil(totalCorrections / BATCH_SIZE);

        for (let i = 0; i < batches; i++) {
          const start = i * BATCH_SIZE;
          const end = Math.min(start + BATCH_SIZE, totalCorrections);
          const batchCorrections = request.corrections.slice(start, end);

          const batchRequest = {
            ...request,
            corrections: batchCorrections
          };

          const batchResults = await this.processCorrectionBatch(batchRequest, start);
          results.push(...batchResults);

          // Call progress callback after each batch completes
          if (request.onProgress) {
            request.onProgress(results, {
              current: end,
              total: totalCorrections,
              batchIndex: i + 1,
              totalBatches: batches
            });
          }

          // Add delay between batches to respect server rate limits
          // Server has minute limit set to 9999 (effectively unlimited)
          // Use 100ms delay for fastest response (actual interval = API time ~1-2s + delay 0.1s)
          if (i < batches - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        return results;
      }

      // For small correction sets, process normally
      return await this.processCorrectionBatch(request, 0);

    } catch (error) {
      console.error('[OpenRouterProxy] Analysis failed:', error);
      throw error;
    }
  }

  private async processCorrectionBatch(request: AIAnalysisRequest, indexOffset: number): Promise<AIAnalysisResult[]> {
    // Use natural language prompts like desktop version
    const messages = this.buildNaturalLanguagePrompt(request);
    const response = await this.makeProxyRequest(messages, request.corrections.length);

    if (!response.choices || !response.choices[0] || !response.choices[0].message) {
      console.error('[OpenRouterProxy] Invalid response structure:', response);
      throw new Error('OpenRouter returned invalid response structure');
    }

    const aiResponse = response.choices[0].message.content || '';

    // Check if response is empty
    if (!aiResponse || aiResponse.trim() === '') {
      console.warn('[OpenRouterProxy] Empty response from AI, returning empty results');
      return [];
    }

    const parsedResult = this.parseNaturalLanguageResponse(aiResponse);

    // Adjust correctionIndex to account for batch offset
    if (indexOffset > 0) {
      parsedResult.forEach(result => {
        result.correctionIndex += indexOffset;
      });
    }

    return parsedResult;
  }

  private async makeProxyRequest(messages: any[], correctionCount: number = 0): Promise<any> {
    const startTime = Date.now();
    const httpClient = HttpClient.getInstance();

    try {
      // Ensure auth token is available
      if (!this.authToken || this.authToken === 'test-free-tier-token') {
        await this.requestAuthToken();
      }

      // Calculate max_tokens based on correction count
      // Each correction needs ~80-120 tokens for JSON response
      let maxTokens = calculateMaxTokens(messages[messages.length - 1].content, this.model);

      if (correctionCount > 0) {
        // Increase max_tokens for corrections analysis
        // Each correction needs approximately 120 tokens for response
        const tokensForCorrections = correctionCount * 120;
        const baseTokens = 800; // Base overhead for JSON structure
        maxTokens = Math.max(maxTokens, baseTokens + tokensForCorrections);

        // Cap at model-specific limits
        if (this.isReasoningModel()) {
          maxTokens = Math.min(maxTokens, 8000);
        } else {
          maxTokens = Math.min(maxTokens, 4000);
        }
      }

      // Increase timeout for large correction sets
      const timeout = correctionCount > 20 ? 90000 : 60000; // 90s for large batches, 60s for small

      const requestBody = {
        model: this.model,
        messages: messages,
        max_tokens: maxTokens,
        temperature: getDefaultTemperature(),
        top_p: 0.9,
        // Use throughput-based provider selection for fastest response
        provider: {
          sort: 'throughput',  // Automatically select fastest provider
          allow_fallbacks: true
        }
      };

      try {
        const data = await httpClient.request<any>(this.serverUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.authToken}`,
            'X-Device-Id': this.deviceId,
            // Add explicit CORS headers for proxy endpoint
            'Origin': window.location.origin,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: requestBody,
          timeout: timeout,
          maxRetries: 3,
          retryDelay: 2000
        });
        
        const elapsed = Date.now() - startTime;
        return data;
        
      } catch (error: any) {
        console.error('[OpenRouterProxy] Request failed:', error);
        
        // Handle specific error cases with Korean messages
        if (error.status === 401) {
          // Try to refresh token once
          this.authToken = '';
          localStorage.removeItem('authToken');
          
          try {
            await this.requestAuthToken();
            
            // Retry with new token
            if (this.authToken && this.authToken !== '') {
              
              const retryData = await httpClient.request<any>(this.serverUrl, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${this.authToken}`,
                  'X-Device-Id': this.deviceId,
                  // Add explicit CORS headers for proxy endpoint
                  'Origin': window.location.origin,
                  'X-Requested-With': 'XMLHttpRequest'
                },
                body: requestBody,
                maxRetries: 2,
                retryDelay: 1000
              });
              
              const elapsed = Date.now() - startTime;
              return retryData;
            }
          } catch (refreshError) {
            console.error('[OpenRouterProxy] Token refresh failed:', refreshError);
          }
          
          throw new Error('인증이 필요합니다. 라이선스를 확인해주세요.');
        } else if (error.status === 403) {
          throw new Error('프리미엄 구독이 필요합니다.');
        } else if (error.status === 429) {
          // Rate limit already handled by HttpClient with retry
          throw new Error('일일 사용 한도를 초과했습니다. 잠시 후 다시 시도해주세요.');
        }
        
        throw error;
      }
    } catch (error) {
      // Track failed AI usage
      const responseTime = Date.now() - startTime;
      await usageTracker.trackAIUsage({
        model: this.model,
        provider: 'openrouter',
        tokens: 0,
        cost: 0,
        responseTime,
        success: false
      });
      
      console.error('[OpenRouterProxy] Request error:', error);
      throw error;
    }
  }

  private buildNaturalLanguagePrompt(request: AIAnalysisRequest): any[] {
    // Natural language system prompt (matches desktop's approach)
    const systemContent = this.isReasoningModel()
      ? `You are a Korean grammar expert specializing in text correction and analysis.

## Your Task:
Analyze Korean text corrections and select the best option for each error.

## Important Guidelines:
1. Evaluate each correction suggestion carefully
2. Select the most appropriate correction
3. **CRITICAL**: If the original text is correct or better than all suggestions, you MUST set keepOriginal: true
4. If all suggestions are incorrect, use the original text or provide a better alternative
5. When keepOriginal is true, set selectedValue to the original text
6. Provide clear reasoning in Korean for each decision
7. Output your response in valid JSON format only

**Important about keepOriginal**:
- Set keepOriginal to true when the original text is already correct
- Set keepOriginal to true when the spell checker made a mistake
- When keepOriginal is true, the selectedValue should be the original text`
      : `Korean grammar expert. Analyze errors and select corrections. Output JSON only.

CRITICAL RULES:
1. If original text is correct, set keepOriginal: true
2. If spell checker made a mistake, set keepOriginal: true
3. When keepOriginal is true, selectedValue must be the original text
4. Respond in Korean for reasoning field`;

    const userContent = this.isReasoningModel()
      ? this.formatDetailedUserPrompt(request)
      : this.formatUserPrompt(request) + `

Choose the best correction for each error. If suggestions are wrong, use original or provide better alternative.

CRITICAL RULES:
1. If original text is correct, set keepOriginal: true
2. When keepOriginal is true, selectedValue must be the original text
3. Use Korean language for the "reasoning" field

JSON format (with exactly ${request.corrections.length} entries):
{
  "corrections": [
    {
      "correctionIndex": 0,
      "selectedValue": "chosen value",
      "isExceptionProcessed": false,
      "keepOriginal": false,
      "reasoning": "이유를 한글로 작성"
    },
    {
      "correctionIndex": 1,
      "selectedValue": "chosen value",
      "isExceptionProcessed": false,
      "keepOriginal": true,
      "reasoning": "원본이 더 적절함"
    }
    ... (continue for all ${request.corrections.length} corrections)
  ]
}

* keepOriginal: Set to true if original text is better than suggestions`;

    return [
      { role: 'system', content: systemContent },
      { role: 'user', content: userContent }
    ];
  }

  private formatUserPrompt(request: AIAnalysisRequest): string {
    const correctionCount = request.corrections.length;
    let prompt = `Context: "${request.contextText}"\n\nErrors to correct (TOTAL: ${correctionCount}):\n`;

    request.corrections.forEach((correction: any, index: number) => {
      const original = correction.original || '';
      const corrected = Array.isArray(correction.corrected)
        ? correction.corrected.join(', ')
        : (correction.corrected || '');

      prompt += `${index + 1}. "${original}" → Options: [${corrected}]\n`;
      if (correction.help) {
        prompt += `   Help: ${correction.help}\n`;
      }
    });

    prompt += `\nIMPORTANT: You MUST provide exactly ${correctionCount} entries in the "corrections" array. Each error above must have a corresponding correction entry.`;

    return prompt;
  }

  private formatDetailedUserPrompt(request: AIAnalysisRequest): string {
    const correctionCount = request.corrections.length;
    let prompt = `## Context Text:
"${request.contextText}"

## Task:
Analyze the following ${correctionCount} Korean text errors and select the best correction for each.

## Errors to Analyze (TOTAL: ${correctionCount}):
`;

    request.corrections.forEach((correction: any, index: number) => {
      const original = correction.original || '';
      const corrected = Array.isArray(correction.corrected)
        ? correction.corrected
        : [correction.corrected || ''];

      prompt += `
### Error ${index + 1}:
- Original text: "${original}"
- Suggested corrections: ${corrected.map((c: string) => `"${c}"`).join(', ')}`;

      if (correction.help) {
        prompt += `\n- Additional context: ${correction.help}`;
      }
    });

    prompt += `

## Response Requirements:
1. For each error, select the most appropriate correction
2. **CRITICAL**: If the original text is correct or better than all suggestions, you MUST set keepOriginal: true
3. If none of the suggestions are correct, keep the original or provide a better alternative
4. When keepOriginal is true, set selectedValue to the original text
5. Explain your reasoning in Korean
6. Return your response in the following JSON format:

{
  "corrections": [
    {
      "correctionIndex": 0,
      "selectedValue": "선택한 값",
      "isExceptionProcessed": false,
      "keepOriginal": false,
      "reasoning": "선택 이유를 한국어로 설명"
    }
  ]
}

* keepOriginal: Set to true if original text is better than all suggestions

## CRITICAL REQUIREMENTS:
- You MUST provide exactly ${correctionCount} entries in the "corrections" array
- Each error listed above MUST have a corresponding correction entry with its correctionIndex (0 to ${correctionCount - 1})
- The "reasoning" field MUST be in Korean
- Output ONLY the JSON response, no additional text
- DO NOT skip any errors - analyze all ${correctionCount} errors`;

    return prompt;
  }

  private parseNaturalLanguageResponse(content: string): AIAnalysisResult[] {
    try {
      // Handle empty responses
      if (!content || content.trim() === '') {
        throw new Error('AI model returned empty response');
      }

      // For reasoning models, try to extract JSON from reasoning text
      if (this.isReasoningModel()) {
        // Look for JSON in various formats
        const jsonPatterns = [
          /```json\s*([\s\S]*?)```/i,
          /```\s*([\s\S]*?)```/i,
          /\{[\s\S]*"corrections"[\s\S]*\}/,
          /\{[\s\S]*"errors"[\s\S]*\}/
        ];

        for (const pattern of jsonPatterns) {
          const match = content.match(pattern);
          if (match) {
            try {
              const jsonStr = match[1] || match[0];
              const parsed = JSON.parse(jsonStr.replace(/```/g, ''));
              
              if (parsed.corrections && Array.isArray(parsed.corrections)) {
                return parsed.corrections;
              }
              if (parsed.errors && Array.isArray(parsed.errors)) {
                // Convert errors format to corrections format
                return parsed.errors.map((error: any, index: number) => ({
                  correctionIndex: index,
                  selectedValue: error.correction || error.corrected,
                  isExceptionProcessed: false,
                  reasoning: error.explanation || error.reasoning || ''
                }));
              }
            } catch (e) {
              // Continue to next pattern
            }
          }
        }
      }

      // Find JSON start position (remove any explanation text)
      let jsonStart = content.indexOf('{');
      if (jsonStart === -1) {
        jsonStart = content.indexOf('[');
      }
      
      if (jsonStart > 0) {
        content = content.substring(jsonStart);
      }

      // Try direct parsing (object format)
      try {
        const direct = JSON.parse(content);
        if (direct.corrections && Array.isArray(direct.corrections)) {
          return direct.corrections;
        }
        // Check for errors format (from grammar check)
        if (direct.errors && Array.isArray(direct.errors)) {
          // Convert errors format to corrections format
          return direct.errors.map((error: any, index: number) => ({
            correctionIndex: index,
            selectedValue: error.correction || error.corrected,
            isExceptionProcessed: false,
            reasoning: error.explanation || error.reasoning || ''
          }));
        }
        // Backward compatibility with array format
        if (Array.isArray(direct)) {
          return direct;
        }
      } catch (e) {
        // Direct parsing failed, try other methods
      }

      // Try code block extraction
      const fence = content.match(/```(?:json)?\s*([\s\S]*?)```/i);
      if (fence && fence[1]) {
        try {
          const parsed = JSON.parse(fence[1].trim());
          if (parsed.corrections && Array.isArray(parsed.corrections)) {
            return parsed.corrections;
          }
          if (parsed.errors && Array.isArray(parsed.errors)) {
            return parsed.errors.map((error: any, index: number) => ({
              correctionIndex: index,
              selectedValue: error.correction || error.corrected,
              isExceptionProcessed: false,
              reasoning: error.explanation || error.reasoning || ''
            }));
          }
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch (e) {
          console.error('[OpenRouterProxy] Code fence JSON parsing failed:', e);
        }
      }

      // Try object extraction with proper nesting support
      const extractedObject = this.extractCompleteJSON(content, '{', '}');
      if (extractedObject) {
        try {
          const parsed = JSON.parse(extractedObject);
          if (parsed.corrections && Array.isArray(parsed.corrections)) {
            return parsed.corrections;
          }
          if (parsed.errors && Array.isArray(parsed.errors)) {
            return parsed.errors.map((error: any, index: number) => ({
              correctionIndex: index,
              selectedValue: error.correction || error.corrected,
              isExceptionProcessed: false,
              reasoning: error.explanation || error.reasoning || ''
            }));
          }
        } catch (e) {
          console.error('[OpenRouterProxy] Object parsing failed:', e);
        }
      }

      // Try array extraction with proper nesting support
      const extractedArray = this.extractCompleteJSON(content, '[', ']');
      if (extractedArray) {
        try {
          const parsed = JSON.parse(extractedArray);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch (e) {
          console.error('[OpenRouterProxy] Array parsing failed:', e);
        }
      }

      // Parsing failed - provide debugging info
      console.error('[OpenRouterProxy] Natural language response parsing failed. Content:', content.substring(0, 500));
      throw new Error('AI response could not be parsed as JSON. Model did not respond in correct format.');
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Unknown error occurred during natural language response parsing');
    }
  }

  isAvailable(): boolean {
    // Always available as it uses server proxy
    return true;
  }

  getModelName(): string {
    return this.model;
  }

  /**
   * Extract complete JSON structure from text by matching brackets
   * @param text - Text containing JSON
   * @param openChar - Opening bracket character ('{' or '[')
   * @param closeChar - Closing bracket character ('}' or ']')
   * @returns Complete JSON string or null if not found
   */
  private extractCompleteJSON(text: string, openChar: string, closeChar: string): string | null {
    const startIndex = text.indexOf(openChar);
    if (startIndex === -1) return null;

    let depth = 0;
    let inString = false;
    let escapeNext = false;

    for (let i = startIndex; i < text.length; i++) {
      const char = text[i];

      // Handle escape sequences
      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\') {
        escapeNext = true;
        continue;
      }

      // Handle string boundaries
      if (char === '"') {
        inString = !inString;
        continue;
      }

      // Only count brackets outside of strings
      if (!inString) {
        if (char === openChar) {
          depth++;
        } else if (char === closeChar) {
          depth--;
          // Found complete structure
          if (depth === 0) {
            return text.substring(startIndex, i + 1);
          }
        }
      }
    }

    return null;
  }

  private isReasoningModel(): boolean {
    const modelLower = this.model.toLowerCase();
    return modelLower.includes('think') ||
           modelLower.includes('reason') ||
           modelLower.includes('o1') ||
           modelLower.includes('deepseek-r1') ||
           modelLower.includes('qwq');
  }

  private createReasoningPrompt(text: string): string {
    return `## Task: Korean Grammar and Spelling Check

Analyze the following Korean text for grammatical and spelling errors.

## Text to Analyze:
"${text}"

## Instructions:
1. Identify spelling errors (맞춤법 오류)
2. Identify grammatical errors (문법 오류)
3. For each error, provide:
   - The original incorrect text
   - The corrected version
   - A brief explanation (maximum 15 characters in Korean)

## Requirements:
- Find up to 10 errors maximum
- Only report clear and definite errors
- Focus on the most important errors first
- Return response in JSON format

## JSON Output Format:
{
  "errors": [
    {
      "original": "incorrect text",
      "correction": "corrected text",
      "explanation": "brief reason in Korean (max 15 chars)"
    }
  ]
}

## Example:
Input: "저는 학교에 갔는대요"
Output: {"errors": [{"original": "갔는대요", "correction": "갔는데요", "explanation": "어미 표기 오류"}]}

Now analyze the provided text and return only the JSON response.`;
  }

  private createStandardPrompt(text: string): string {
    return `"${text}"

위 텍스트에서 맞춤법/문법 오류 찾아 JSON:
{"errors": [{"original": "틀린단어", "correction": "올바른단어", "explanation": "이유(15자내)"}]}

최대 10개, 명백한 오류만.`;
  }

  private getReasoningSystemPrompt(): string {
    return `You are an expert Korean language specialist with deep knowledge of Korean grammar, spelling, and usage rules.

Your expertise includes:
- Korean orthography (한글 맞춤법)
- Grammar rules (문법 규칙)
- Proper spacing (띄어쓰기)
- Word usage and collocations

IMPORTANT INSTRUCTIONS:
1. Think through each potential error carefully
2. Only flag definite errors, not stylistic preferences
3. Provide clear, concise explanations in Korean
4. Output ONLY valid JSON format
5. Do not include any text outside the JSON structure`;
  }

}